import { Formik, Form, useFormikContext } from 'formik';
import { noop } from 'lodash';
import { useCurrentStateAndParams } from '@uirouter/react';

import { notifySuccess } from '@/portainer/services/notifications';
import { Registry } from '@/react/portainer/registries/types/registry';

import { Widget, WidgetBody } from '@@/Widget';
import { Alert } from '@@/Alert';
import { LoadingButton } from '@@/buttons';
import { confirm } from '@@/modals/confirm';
import { ModalType } from '@@/modals/Modal/types';
import { buildConfirmButton } from '@@/modals/utils';

import { useAddon } from '../queries/useAddon';
import { useAddonRegistries } from '../queries/useAddonRegistries';
import { useAddonEnvironmentId } from '../useAddonEnvironmentId';
import { useUpgradeAddon } from '../queries/useUpgradeAddon';
import { ConfigurationStep } from '../InstallView/ConfigurationStep';
import { InstallFormValues } from '../InstallView/types';
import {
  AddonValues,
  hasValueChanges,
  toAddonValues,
  toHelmValues,
} from '../InstallView/addonValues';
import {
  AddonImage,
  imageRepositoryFor,
  splitImageRepository,
} from '../InstallView/imagePullRegistry';
import { isUpgradeRequested } from '../upgradeDeepLink';
import { getAddonLifecycleState } from '../addonLifecycleState';

import { useAddonReleaseValues } from './queries/useAddonReleaseValues';

export function ConfigurationTab() {
  const { params } = useCurrentStateAndParams();
  const addonId = String(params.id ?? '');
  const addonQuery = useAddon(addonId);
  const record = addonQuery.data?.record;
  const chartVersion = record?.chartVersion;
  const releaseValuesQuery = useAddonReleaseValues(addonId);

  const environmentQuery = useAddonEnvironmentId();
  const isEnvError = environmentQuery.isError;
  // Success only: a failed lookup is not evidence that no environment exists,
  // and the fallback below submits chart defaults over the running values.
  const valuesUnreadable = environmentQuery.isSuccess && !environmentQuery.data;

  const { isInProgress } = getAddonLifecycleState(addonQuery.data);
  // Editable with unreadable values too, or these deployments could never
  // upgrade; the baseline falls back to chart defaults.
  const editable =
    (releaseValuesQuery.isSuccess || valuesUnreadable) && !isInProgress;

  // Auto-select newer version only when arriving from Upgrade action
  const wantsUpgrade = isUpgradeRequested(params);
  const latest = record?.availableVersions?.[0];
  const initialVersion =
    wantsUpgrade && record?.upgradeAvailable && latest
      ? latest
      : (chartVersion ?? '');

  // The release's current values are the form's baseline, so an unchanged field
  // is resubmitted as-is instead of reverting to the chart default on save.
  const baseline = toAddonValues(releaseValuesQuery.data);

  // The release stores one reference, so the registry it names is recovered before
  // the form mounts. Done here rather than in an effect: the form seeds once, and a
  // later write would overwrite whatever had been typed in the meantime.
  const environmentId = environmentQuery.data;
  const registriesQuery = useAddonRegistries(environmentId);
  const registries = registriesQuery.data ?? [];
  // Waited for, because the split needs them and the form seeds once. A failed
  // lookup (or no environment to look up against) settles too, so a registry
  // outage leaves the image showing its full reference rather than blocking.
  const registriesReady = !environmentId || registriesQuery.isFetched;
  const baselineImage = splitImageRepository(
    baseline.imageRepository,
    registries
  );

  // The chart source is deliberately absent from the form: it is fixed at install
  // time, so it is read live from the record (see useAddonChartSource). Seeding it
  // into Formik would freeze it at mount and miss a source that arrives on a
  // later poll.
  const initialValues: InstallFormValues = {
    version: initialVersion,
    chart: '',
    chartConfirmed: '',
    imageRegistryId: baselineImage.registryId,
    imagePath: baselineImage.path,
    values: baseline,
    teams: [],
    denyPortainerAccess: {},
  };

  return (
    <div className="row">
      <div className="col-sm-12">
        <Widget aria-label="Configuration">
          {/* Seeded from the release's values, so it waits for them and mounts
              once. Reinitialising a live form would discard in-progress edits on
              the next refetch. */}
          <WidgetBody
            loading={
              addonQuery.isLoading ||
              !releaseValuesQuery.isSettled ||
              !registriesReady
            }
          >
            {addonQuery.isError && (
              <Alert color="error">
                Unable to load the add-on&apos;s chart versions.
              </Alert>
            )}
            {valuesUnreadable && (
              <Alert
                color="info"
                title="Current configuration unavailable"
                className="mb-2"
              >
                The cluster this add-on runs in is not connected to Portainer as
                an environment, so the values it is currently running with
                cannot be read. The values shown are the chart defaults, and
                submitting will apply them as-is.
              </Alert>
            )}
            {isEnvError && (
              <Alert
                color="error"
                title="Configuration cannot be edited"
                className="mb-2"
              >
                Portainer could not work out which environment to read this
                add-on through, so its current values are unknown. Editing is
                disabled rather than risk overwriting them with chart defaults.
              </Alert>
            )}
            {releaseValuesQuery.isError && (
              <Alert
                color="warn"
                title="Current configuration unavailable"
                className="mb-2"
              >
                The add-on&apos;s configuration could not be loaded - the Helm
                release may have been removed outside Portainer, or the
                installation may have failed. The values shown are the chart
                defaults.
              </Alert>
            )}
            {addonQuery.data && !addonQuery.isError && registriesReady && (
              <Formik<InstallFormValues>
                initialValues={initialValues}
                onSubmit={noop}
              >
                <Form noValidate>
                  <UpgradeFormBody
                    addonId={addonId}
                    editable={editable}
                    installedVersion={chartVersion ?? ''}
                    baseline={baseline}
                    baselineImage={baselineImage}
                    registries={registries}
                    displayName={addonQuery.data?.displayName ?? 'add-on'}
                    // The form may render from a cached "none" while the
                    // refetch runs; submitting on it applies chart defaults
                    // over values that are readable again.
                    resolvingEnvironment={environmentQuery.isFetching}
                  />
                </Form>
              </Formik>
            )}
          </WidgetBody>
        </Widget>
      </div>
    </div>
  );
}

function UpgradeFormBody({
  addonId,
  editable,
  installedVersion,
  baseline,
  baselineImage,
  registries,
  displayName,
  resolvingEnvironment,
}: {
  addonId: string;
  editable: boolean;
  installedVersion: string;
  baseline: AddonValues;
  baselineImage: AddonImage;
  registries: Array<Registry>;
  displayName: string;
  resolvingEnvironment: boolean;
}) {
  const { values } = useFormikContext<InstallFormValues>();
  const upgradeMutation = useUpgradeAddon(addonId);

  // The image lives outside AddonValues, so its change is checked separately.
  const imageChanged =
    values.imageRegistryId !== baselineImage.registryId ||
    values.imagePath.trim() !== baselineImage.path.trim();

  const isUpgrade = values.version !== installedVersion;
  const changed =
    isUpgrade || imageChanged || hasValueChanges(values.values, baseline);

  return (
    <>
      <ConfigurationStep readOnly={!editable} chartSourceReadOnly />

      {editable && (
        <div className="mt-4 flex">
          <LoadingButton
            className="!ml-0"
            type="button"
            color="primary"
            disabled={!changed || resolvingEnvironment}
            isLoading={upgradeMutation.isLoading}
            loadingText="Applying"
            onClick={handleClick}
            data-cy="addon-config-upgrade-button"
          >
            {isUpgrade ? 'Upgrade' : 'Apply changes'}
          </LoadingButton>
        </div>
      )}
    </>
  );

  async function handleClick() {
    // Version change restarts the add-on; same-version edit applies in place
    if (isUpgrade) {
      const confirmed = await confirm({
        title: 'Upgrade add-on',
        modalType: ModalType.Warn,
        message: `Upgrade ${displayName} from v${installedVersion} to v${values.version}? This will restart the add-on.`,
        confirmButton: buildConfirmButton('Upgrade'),
      });
      if (!confirmed) {
        return;
      }
    }

    upgradeMutation.mutate(
      {
        version: values.version,
        imageRegistryId: values.imageRegistryId,
        values: toHelmValues({
          ...values.values,
          imageRepository: imageRepositoryFor(
            { registryId: values.imageRegistryId, path: values.imagePath },
            registries
          ),
        }),
      },
      {
        onSuccess: () => notifySuccess('Add-on upgrade started', displayName),
      }
    );
  }
}
